import React, { useEffect, useState } from "react";
import {
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  Toggle,
} from "@fluentui/react";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import TextInput from "@/ui-controls/TextInput";
import CustomerComboBox, {
  findCustomerName,
} from "@/ui-controls/CustomerComboBox";
import UserComboBox, {
  findUserEmail,
} from "@/ui-controls/UserComboBox";
import { Grid } from "fluentui-react-grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createIntegrationConfiguration,
  createConfigurationProperty,
} from "@/api/integrations";
import ConfigurationProperties, {
  PropertyEntry,
} from "@/components/integrations/configurations/ConfigurationProperties";

import { IntegrationDto } from "@/api/integrations";

interface Props {
  integrationId?: number;
  integrations?: IntegrationDto[];
  onSave?: () => void;
  onClose?: () => void;
}

const scopeOptions: IDropdownOption[] = [
  { key: 1, text: "Global" },
  { key: 2, text: "Customer" },
  { key: 3, text: "User" },
];

const authOptions: IDropdownOption[] = [
  { key: 1, text: "API Key" },
  { key: 2, text: "Username / Password" },
  { key: 3, text: "Username / Password / Company Code" },
];

const NewConfigurationForm = (props: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [properties, setProperties] = useState<PropertyEntry[]>([]);

  const integrationOptions: IDropdownOption[] = (props.integrations || []).map(
    (i) => ({ key: i.id, text: `${i.code} - ${i.name}` })
  );

  const formik = useFormik({
    initialValues: {
      integrationId: props.integrationId ?? 0,
      scopeType: 1,
      customerId: "",
      userId: "",
      authType: 1,
      apiKey: "",
      username: "",
      password: "",
      companyCode: "",
      configJson: "",
      configName: "",
      effectiveFrom: new Date().toISOString().split("T")[0],
      effectiveTo: "",
      isActive: true,
      isTestMode: false,
    },
    validationSchema: Yup.object({
      integrationId: Yup.number()
        .required("Integration is required")
        .positive("Integration is required"),
      configName: Yup.string().max(250),
      effectiveFrom: Yup.string().required("Effective from is required"),
    }),
    onSubmit: async (values) => {
      setIsSaving(true);
      setErrorMessage("");
      try {
        const configId = await createIntegrationConfiguration({
          integrationId: values.integrationId,
          scopeType: values.scopeType,
          customerId: values.customerId ? Number(values.customerId) : null,
          userId: values.userId ? Number(values.userId) : null,
          authType: values.authType,
          apiKey: values.apiKey || null,
          username: values.username || null,
          password: values.password || null,
          companyCode: values.companyCode || null,
          configJson: values.configJson || null,
          configName: values.configName || null,
          effectiveFrom: values.effectiveFrom,
          effectiveTo: values.effectiveTo || null,
          isActive: values.isActive,
          isTestMode: values.isTestMode || null,
        });

        const validProps = properties.filter(
          (p) => p.propertyName.trim() && p.propertyValue.trim()
        );
        for (const prop of validProps) {
          await createConfigurationProperty({
            integrationConfigurationId: configId,
            propertyName: prop.propertyName.trim(),
            propertyValue: prop.propertyValue,
          });
        }

        props.onSave?.();
      } catch (error: any) {
        const data = error?.response?.data;
        setErrorMessage(data?.detail || "Failed to create configuration.");
      } finally {
        setIsSaving(false);
      }
    },
  });

  const getIntegrationCode = (): string => {
    const integration = (props.integrations || []).find(
      (i) => i.id === formik.values.integrationId
    );
    return integration?.code ?? "";
  };

  useEffect(() => {
    const code = getIntegrationCode();
    if (!code) {
      formik.setFieldValue("configName", "");
      return;
    }

    const scope = formik.values.scopeType;
    const customerId = formik.values.customerId
      ? Number(formik.values.customerId)
      : 0;
    const userId = formik.values.userId ? Number(formik.values.userId) : 0;

    let name = "";
    if (scope === 1) {
      name = `PCS GLOBAL - ${code}`;
    } else if (scope === 2 && customerId) {
      const customerName = findCustomerName(customerId) ?? "";
      name = customerName ? `${customerName} - ${code}` : "";
    } else if (scope === 3 && customerId && userId) {
      const customerName = findCustomerName(customerId) ?? "";
      const userEmail = findUserEmail(customerId, userId) ?? "";
      name =
        customerName && userEmail
          ? `${customerName} - ${userEmail} - ${code}`
          : "";
    }

    formik.setFieldValue("configName", name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.integrationId,
    formik.values.scopeType,
    formik.values.customerId,
    formik.values.userId,
  ]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        {!props.integrationId && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <Dropdown
                label="Integration"
                placeholder="Select an integration"
                options={integrationOptions}
                selectedKey={formik.values.integrationId || undefined}
                onChange={(_, option) =>
                  formik.setFieldValue("integrationId", option?.key ?? 0)
                }
                disabled={isSaving}
                errorMessage={
                  formik.errors.integrationId && formik.touched.integrationId
                    ? formik.errors.integrationId
                    : undefined
                }
              />
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <Dropdown
              label="Scope Type"
              options={scopeOptions}
              selectedKey={formik.values.scopeType}
              onChange={(_, option) =>
                formik.setFieldValue("scopeType", option?.key ?? 1)
              }
              disabled={isSaving}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <Dropdown
              label="Auth Type"
              options={authOptions}
              selectedKey={formik.values.authType}
              onChange={(_, option) =>
                formik.setFieldValue("authType", option?.key ?? 1)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <TextInput
              label="Config Name"
              baseTypeProps={{
                readOnly: true,
                ...formik.getFieldProps("configName"),
              }}
            />
          </Grid.Col>
        </Grid.Row>
        {formik.values.scopeType >= 2 && (
          <Grid.Row>
            <Grid.Col sizeSm={6}>
              <CustomerComboBox
                selectedCustomerId={
                  formik.values.customerId
                    ? Number(formik.values.customerId)
                    : undefined
                }
                onChange={(id) =>
                  formik.setFieldValue("customerId", id ? String(id) : "")
                }
                disabled={isSaving}
              />
            </Grid.Col>
            {formik.values.scopeType >= 3 && (
              <Grid.Col sizeSm={6}>
                <UserComboBox
                  customerId={
                    formik.values.customerId
                      ? Number(formik.values.customerId)
                      : undefined
                  }
                  selectedUserId={
                    formik.values.userId
                      ? Number(formik.values.userId)
                      : undefined
                  }
                  onChange={(id) =>
                    formik.setFieldValue("userId", id ? String(id) : "")
                  }
                  disabled={isSaving}
                />
              </Grid.Col>
            )}
          </Grid.Row>
        )}
        {formik.values.authType === 1 && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="API Key"
                baseTypeProps={{
                  disabled: isSaving,
                  type: "password",
                  canRevealPassword: true,
                  ...formik.getFieldProps("apiKey"),
                }}
              />
            </Grid.Col>
          </Grid.Row>
        )}
        {(formik.values.authType === 2 || formik.values.authType === 3) && (
          <Grid.Row>
            <Grid.Col sizeSm={6}>
              <TextInput
                label="Username"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("username"),
                }}
              />
            </Grid.Col>
            <Grid.Col sizeSm={6}>
              <TextInput
                label="Password"
                baseTypeProps={{
                  disabled: isSaving,
                  type: "password",
                  canRevealPassword: true,
                  ...formik.getFieldProps("password"),
                }}
              />
            </Grid.Col>
          </Grid.Row>
        )}
        {formik.values.authType === 3 && (
          <Grid.Row>
            <Grid.Col sizeSm={6}>
              <TextInput
                label="Company Code"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("companyCode"),
                }}
              />
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <TextInput
              label="Config JSON"
              baseTypeProps={{
                disabled: isSaving,
                multiline: true,
                rows: 4,
                ...formik.getFieldProps("configJson"),
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <ConfigurationProperties
              properties={properties}
              onChange={setProperties}
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <TextInput
              label="Effective From"
              baseTypeProps={{
                disabled: isSaving,
                type: "date",
                ...formik.getFieldProps("effectiveFrom"),
              }}
              hasError={
                formik.errors.effectiveFrom && formik.touched.effectiveFrom
              }
              errorMessage={formik.errors.effectiveFrom}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <TextInput
              label="Effective To"
              baseTypeProps={{
                disabled: isSaving,
                type: "date",
                ...formik.getFieldProps("effectiveTo"),
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <Toggle
              label="Active"
              checked={formik.values.isActive}
              onChange={(_, checked) =>
                formik.setFieldValue("isActive", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <Toggle
              label="Test Mode"
              checked={formik.values.isTestMode}
              onChange={(_, checked) =>
                formik.setFieldValue("isTestMode", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={() => props.onClose?.()}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

export default NewConfigurationForm;
